import CustomErrorBoundary from '@/app/[lng]/ErrorBoundary'
import { useDeleteProductServicesMutation } from '@/app/globalRedux/api/productServiceSlice'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import { ProductServices } from '@/app/types/productServiceType'
import { AvailableMonthList } from '@/components/AvailableMonthList'
import DeleteDialog from '@/components/ui/DeleteDialog'
import { Dialog, DialogTrigger } from '@/components/ui/dialog'
import { Tooltip } from '@mui/material'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'
import AddorEditProductModal from '../(modal)/AddOrEditProductModal'
import AskQuote from '../(modal)/AskQuote'

const ProductServiceCard = ({
  product,
  visible,
}: {
  product: ProductServices
  visible?: boolean
}) => {
  const { t } = useTranslation()
  const pathName = usePathname()
  const isLoggedIn = useSelector((state: RootState) => state.user.loggedIn)
  const [deleteProductServices] = useDeleteProductServicesMutation()
  const [open, setOpen] = useState<boolean>(false)
  const [openClone, setOpenClone] = useState<boolean>(false)

  const deleteProduct = async () => {
    if (product?.id) {
      const deleteResponse: any = await deleteProductServices(product?.id)
      if (deleteResponse?.status == 'success') {
        toast.info(t('Product Deleted SuccessFully!'))
      }
    }
  }

  const showQuote = (isLoggedIn: boolean, pathName: string) => {
    return isLoggedIn && !pathName.includes(`/myfructidor/edit-product-service`)
  }
  return (
    <div
      className="text-sm flex basis-2/12 gap-8 relative overflow-hidden p-6 border-[#ebebeb] border-b border-solid mobile:flex-col mobile:gap-[10px]"
      key={product.id}
    >
      <div className="flex flex-col gap-3 mobile:hidden">
        <div className="!w-[120px] !h-[120px]">
          <Image
            width={120}
            height={120}
            loading="lazy"
            src={product?.image ?? '/assets/Icons/noImage/noImage2.svg'}
            alt={product?.product?.name ?? ''}
            className="!w-[120px] !h-[120px] object-cover border rounded-sm"
          />
        </div>
        {showQuote(isLoggedIn ?? false, pathName) && (
          <AskQuote productId={product?.id} />
        )}
      </div>
      <div className="basis-10/12 flex flex-col gap-y-3">
        <div className=" flex flex-row items-center justify-start gap-2">
          <div className="flex mobile:flex-col gap-2">
            <div className=" text-base font-ns_bold not-italic leading-normal tracking-normal text-left text-primary_text">
              {product?.product?.name}
            </div>
            <div className="flex flex-row items-center justify-start gap-2">
              {product?.product_type?.name && (
                <div className="">
                  <span className=" border bg-chip_bg border-chip_border rounded-full  px-2">
                    {t(product?.product_type?.name)}
                  </span>
                </div>
              )}
              {product?.priority && Number(product?.priority) <= 3 && (
                <div className="text-xs px-1 py-0.5 bg-priority_bg border rounded-[3px] border-priority_border ">
                  {Number(product?.priority) === 1 ? (
                    <span>
                      1<sup>st </sup>
                    </span>
                  ) : Number(product?.priority) === 2 ? (
                    <span>
                      2<sup>nd </sup>
                    </span>
                  ) : Number(product?.priority) === 3 ? (
                    <span>
                      3<sup>rd </sup>
                    </span>
                  ) : (
                    ''
                  )}
                  {t('Product')}
                </div>
              )}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4 mr-4">
            {visible && (
              <Dialog open={openClone} onOpenChange={setOpenClone}>
                <DialogTrigger asChild>
                  <Image
                    loading="lazy"
                    src="/assets/Icons/Clone.svg"
                    height={20}
                    width={20}
                    alt="clone"
                    className="cursor-pointer"
                  />
                </DialogTrigger>
                <AddorEditProductModal
                  setOpen={setOpenClone}
                  functionality={'Clone'}
                  id={product?.id}
                />
              </Dialog>
            )}
            {visible && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Image
                    loading="lazy"
                    src="/assets/Icons/Edit.svg"
                    height={20}
                    width={20}
                    className="cursor-pointer"
                    alt="edit"
                  />
                </DialogTrigger>
                <AddorEditProductModal
                  setOpen={setOpen}
                  functionality={'Edit'}
                  id={product?.id}
                />
              </Dialog>
            )}

            {visible && !product?.is_sponsored && (
              <DeleteDialog
                onConfirm={() => {
                  deleteProduct()
                }}
                title={'Delete Product'}
                message={'Product'}
              />
            )}
          </div>
        </div>
        <div className="flex mobile:gap-3">
          {/* Mobile View Start */}
          <div className="mobile:flex mobile:flex-col gap-3 hidden">
            <div className="!w-[120px] !h-[120px]">
              <Image
                width={120}
                height={120}
                loading="lazy"
                src={product?.image ?? '/assets/Icons/noImage/noImage2.svg'}
                alt={product?.product?.name ?? ''}
                className="!w-[120px] !h-[120px] object-cover border rounded-sm"
              />
            </div>
            {showQuote(isLoggedIn ?? false, pathName) && (
              <AskQuote productId={product?.id} />
            )}
          </div>
          {/* Mobile View End */}
          <div className="flex flex-col gap-3 w-full">
            <div className=" flex flex-row items-start mobile:flex-col mobile:gap-2">
              <div className="flex flex-col basis-1/3">
                <h2 className="font-ns_bold not-italic leading-normal tracking-normal text-left text-primary_text">
                  {t('ORIGIN')}
                </h2>
                <div className="flex gap-1 text-primary-foreground pt-1">
                  {product?.origin?.country?.length == 1 && (
                    <p>{product?.origin?.country[0]}</p>
                  )}
                  {product?.origin?.country?.length > 1 && (
                    <>
                      {product?.origin?.country[0]}
                      <Tooltip
                        title={String(
                          product?.origin?.country
                            ?.slice(1, product?.origin?.country?.length)
                            .map((item) => item)
                        )}
                        arrow
                      >
                        <p className="cursor-pointer font-bold">{` +${product?.origin?.country?.length - 1}`}</p>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
              <div className=" flex flex-col basis-1/3">
                <h5 className=" font-ns_bold not-italic leading-normal tracking-normal text-left text-primary_text">
                  {t('Quantity')}
                </h5>
                <div className=" flex flex-row gap-2">
                  <div className="text-primary-foreground ">
                    {product?.tonnage ?? t('NA')} {product?.unit?.name}
                  </div>
                </div>
              </div>
              <div className="flex flex-col basis-1/3">
                <div>
                  <h5 className="font-ns_bold not-italic leading-normal tracking-normal text-left text-primary_text">
                    {t('Certification')}
                  </h5>
                  <div className="flex flex-wrap gap-1 text-primary-foreground">
                    {product?.certifications &&
                    product.certifications.length > 0
                      ? product.certifications
                          .map((item) => ({
                            id: item.id,
                            name: item.name,
                          }))
                          .map((item, index) => (
                            <CustomErrorBoundary key={item.id}>
                              <span
                                style={{
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'wrap',
                                }}
                              >
                                {item.name}
                                {index < product.certifications.length - 1 &&
                                  ', '}
                              </span>
                            </CustomErrorBoundary>
                          ))
                      : t('NA')}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row items-start mobile:flex-col mobile:items-start mobile:gap-2">
              <div className="flex flex-col basis-1/3">
                <h5 className="font-ns_bold text-primary_text">
                  {t('Variety')}
                </h5>
                <div className="text-primary-foreground flex flex-row flex-wrap gap-1 w-full truncate">
                  {product?.varieties && product?.varieties?.length > 0
                    ? product?.varieties?.map((e, index: number) => (
                        <CustomErrorBoundary key={e.id}>
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              textOverflow: 'wrap',
                            }}
                            key={e.id}
                          >
                            {e.name}
                            {index <
                              (product?.varieties
                                ? product?.varieties?.length - 1
                                : -1) && ', '}
                          </span>
                        </CustomErrorBoundary>
                      ))
                    : t('NA')}
                </div>
              </div>

              <div className="flex flex-col  basis-2/3 mobile:basis-[100%]">
                <h5 className=" font-ns_bold not-italic leading-normal tracking-normal text-left text-primary_text">
                  {t('AVAILABILITY')}
                </h5>
                <div className=" flex flex-row mt-1 text-primary-foreground w-full gap-3 ">
                  <AvailableMonthList
                    availableMonths={product?.available_months?.months ?? []}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {product?.is_sponsored && (
        <div className="absolute -right-9 bottom-5 bg-sponsor_color -rotate-45 text-xs flex px-8">
          <Image
            src="/assets/Icons/blackStar.png"
            alt=""
            width={17.4}
            height={17.4}
          />
          {t('Sponsored')}
        </div>
      )}
    </div>
  )
}

export default ProductServiceCard
